/**
 * FilterBar Component - Web Version (Simplified with Modern Calendar)
 *
 * Simple filter controls for FleetHub orders with:
 * - Sort direction toggle (arrow button)
 * - Date range filter (calendar button with inline calendar)
 *
 * @module components/FilterBar
 */

import { FC, useState, useEffect, ReactNode } from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  TouchableOpacity,
  Modal,
  Pressable,
  useWindowDimensions,
} from "react-native"
import dayjs from "dayjs"
import DatePicker from "react-native-ui-datepicker"
import Icon from "react-native-vector-icons/MaterialIcons"

import { Button } from "@/components/Button"
import { Text } from "@/components/Text"
import { colors, spacing } from "@/theme"

/**
 * Filter State Types
 */
export type SortDirectionOption = "desc" | "asc"

export interface DateRange {
  startDate: string | null // ISO string
  endDate: string | null // ISO string
}

export interface FilterState {
  sortDirection: SortDirectionOption
  dateRange: DateRange
}

export interface FilterBarProps {
  /**
   * Current filter state
   */
  filters: FilterState

  /**
   * Callback when sort direction changes (applies immediately)
   */
  onSortDirectionChange: (direction: SortDirectionOption) => void

  /**
   * Callback when date range filter is applied
   */
  onDateRangeChange: (range: DateRange) => void

  /**
   * Callback when date filter is cleared
   */
  onClearDateFilter: () => void

  /**
   * Whether date filter is active
   */
  hasActiveDateFilter: boolean

  /**
   * Whether a number plate search filter is currently active
   */
  isSearchActive?: boolean

  /**
   * Optional number plate search element to display between filters and booking button
   */
  searchPlate?: ReactNode

  /**
   * Optional booking button to display on the right
   */
  bookingButton?: ReactNode

  /**
   * Opt-in (PSP view, PLA-861): location filter element rendered after the
   * date button. Absent → bar renders exactly as before.
   */
  locationFilter?: ReactNode

  /**
   * Opt-in: whether the location filter is narrowing the list (lights the
   * mobile "Filtern" badge). Defaults to false.
   */
  isLocationFilterActive?: boolean
}

/**
 * Date picker range interface
 */
interface DateRangePickerValue {
  startDate: dayjs.Dayjs | undefined
  endDate: dayjs.Dayjs | undefined
}

/**
 * Date Range Calendar Modal Component
 */
interface DateRangeCalendarProps {
  visible: boolean
  onClose: () => void
  onApply: (range: DateRange) => void
  onClear: () => void
  initialRange: DateRange
}

const DateRangeCalendar: FC<DateRangeCalendarProps> = ({
  visible,
  onClose,
  onApply,
  onClear,
  initialRange,
}) => {
  const { width } = useWindowDimensions()
  const isMobile = width < 768

  const [selectedRange, setSelectedRange] = useState<DateRangePickerValue>({
    startDate: initialRange.startDate ? dayjs(initialRange.startDate) : undefined,
    endDate: initialRange.endDate ? dayjs(initialRange.endDate) : undefined,
  })
  const [error, setError] = useState<string | null>(null)

  // Update local state when initialRange changes
  useEffect(() => {
    setSelectedRange({
      startDate: initialRange.startDate ? dayjs(initialRange.startDate) : undefined,
      endDate: initialRange.endDate ? dayjs(initialRange.endDate) : undefined,
    })
  }, [initialRange.startDate, initialRange.endDate])

  const handleApply = () => {
    // Validate dates
    if (!selectedRange.startDate || !selectedRange.endDate) {
      setError("Bitte wähl Start- und Enddatum")
      return
    }

    // Ensure dates are dayjs objects
    const start = dayjs(selectedRange.startDate)
    const end = dayjs(selectedRange.endDate)

    // Check date range not too large (max 1 year)
    const daysDiff = end.diff(start, "day")
    if (daysDiff > 365) {
      setError("Zeitraum darf maximal 1 Jahr betragen")
      return
    }

    onApply({
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    })
    setError(null)
    onClose()
  }

  const handleClear = () => {
    setSelectedRange({ startDate: undefined, endDate: undefined })
    setError(null)
    onClear()
    onClose()
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={$modalOverlay} onPress={onClose}>
        <Pressable style={$modalContent} onPress={(e) => e.stopPropagation()}>
          <View style={$modalHeader}>
            <Text style={$modalTitle}>Abholdatum filtern</Text>
            <TouchableOpacity onPress={onClose} activeOpacity={0.7}>
              <Icon name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <View style={$modalBody}>
            <View style={$calendarWrapper}>
              <DatePicker
                mode="range"
                startDate={selectedRange.startDate}
                endDate={selectedRange.endDate}
                onChange={(params: any) => {
                  setSelectedRange({
                    startDate: params.startDate,
                    endDate: params.endDate,
                  })
                  setError(null)
                }}
                components={{
                  IconPrev: <Icon name="chevron-left" size={24} color={colors.text} />,
                  IconNext: <Icon name="chevron-right" size={24} color={colors.text} />,
                }}
                styles={{
                  // Start and end dates
                  selected: {
                    backgroundColor: colors.primary,
                  },
                  selected_label: {
                    color: colors.surface,
                    fontWeight: "600",
                  },
                  range_start: {
                    backgroundColor: colors.primary,
                  },
                  range_start_label: {
                    color: colors.surface,
                    fontWeight: "600",
                  },
                  range_end: {
                    backgroundColor: colors.primary,
                  },
                  range_end_label: {
                    color: colors.surface,
                    fontWeight: "600",
                  },
                  // Dates in between
                  range_fill: {
                    backgroundColor: colors.palette.neutral200, // Light gray background for range
                  },
                  range_middle: {
                    backgroundColor: "transparent",
                  },
                  range_middle_label: {
                    color: colors.text,
                  },
                  // Today indicator
                  today: {
                    borderColor: colors.primary,
                    borderWidth: 2,
                  },
                  // Navigation buttons (fix white on white)
                  button_prev: {
                    backgroundColor: "transparent",
                  },
                  button_next: {
                    backgroundColor: "transparent",
                  },
                  // Month/Year selectors
                  month_selector_label: {
                    color: colors.text,
                    fontWeight: "600",
                  },
                  year_selector_label: {
                    color: colors.text,
                    fontWeight: "600",
                  },
                }}
              />
            </View>

            {error && (
              <View style={$errorContainer}>
                <Icon name="error-outline" size={18} color={colors.error} />
                <Text style={$errorText}>{error}</Text>
              </View>
            )}
          </View>

          <View style={isMobile ? $modalFooterMobile : $modalFooter}>
            <Button
              text="Filter löschen"
              onPress={handleClear}
              preset="default"
              style={isMobile ? $modalButtonMobile : $modalButton}
            />
            <View
              style={isMobile ? $modalButtonGroupMobile : { flexDirection: "row", gap: spacing.sm }}
            >
              <Button
                text="Abbrechen"
                onPress={onClose}
                preset="default"
                style={isMobile ? $modalButtonMobile : $modalButton}
              />
              <Button
                text="Übernehmen"
                onPress={handleApply}
                preset="filled"
                style={
                  isMobile
                    ? [$modalButtonMobile, $modalButtonPrimary]
                    : [$modalButton, $modalButtonPrimary]
                }
              />
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  )
}

/**
 * FilterBar Component - Simplified to 2 icon buttons
 */
export const FilterBar: FC<FilterBarProps> = ({
  filters,
  onSortDirectionChange,
  onDateRangeChange,
  onClearDateFilter,
  hasActiveDateFilter,
  isSearchActive = false,
  searchPlate,
  bookingButton,
  locationFilter,
  isLocationFilterActive = false,
}) => {
  const { width } = useWindowDimensions()
  const isMobile = width < 768

  const [showDateCalendar, setShowDateCalendar] = useState(false)
  const [sortHovered, setSortHovered] = useState(false)
  const [dateHovered, setDateHovered] = useState(false)
  const [filtersExpanded, setFiltersExpanded] = useState(false)
  const [filterHovered, setFilterHovered] = useState(false)

  const handleSortToggle = () => {
    const newDirection = filters.sortDirection === "desc" ? "asc" : "desc"
    console.log("[FilterBar] Toggling sort direction:", newDirection)
    onSortDirectionChange(newDirection)
  }

  const handleDateRangeApply = (range: DateRange) => {
    console.log("[FilterBar] Applying date range:", range)
    onDateRangeChange(range)
  }

  const handleClearDateFilter = () => {
    console.log("[FilterBar] Clearing date filter")
    onClearDateFilter()
  }

  // Shared filter buttons used in both mobile expanded and desktop inline
  const sortButton = (
    <TouchableOpacity
      style={[$iconButton, sortHovered && $iconButtonHover, isMobile && $iconButtonMobile]}
      onPress={handleSortToggle}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={
        filters.sortDirection === "desc" ? "Sortierung: Neueste" : "Sortierung: Älteste"
      }
      accessibilityHint="Sortierreihenfolge umkehren"
      // @ts-ignore - Web-specific
      onMouseEnter={() => setSortHovered(true)}
      onMouseLeave={() => setSortHovered(false)}
    >
      <Icon
        name={filters.sortDirection === "desc" ? "arrow-downward" : "arrow-upward"}
        size={isMobile ? 18 : 20}
        color={colors.text}
      />
      <Text style={[$iconButtonText, isMobile && $iconButtonTextMobile]}>
        {filters.sortDirection === "desc" ? "Neueste" : "Älteste"}
      </Text>
    </TouchableOpacity>
  )

  const dateButton = (
    <TouchableOpacity
      style={[
        $iconButton,
        dateHovered && $iconButtonHover,
        hasActiveDateFilter && $iconButtonActive,
        isMobile && $iconButtonMobile,
      ]}
      onPress={() => setShowDateCalendar(true)}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel="Datum filtern"
      accessibilityHint={
        hasActiveDateFilter
          ? "Datumsfilter aktiv, tippen zum Ändern"
          : "Tippen zum Filtern nach Abholdatum"
      }
      // @ts-ignore - Web-specific
      onMouseEnter={() => setDateHovered(true)}
      onMouseLeave={() => setDateHovered(false)}
    >
      <View style={{ position: "relative" }}>
        <Icon name="calendar-today" size={isMobile ? 18 : 20} color={colors.text} />
        {hasActiveDateFilter && <View style={$activeBadge} />}
      </View>
      <Text style={[$iconButtonText, isMobile && $iconButtonTextMobile]}>Datum</Text>
    </TouchableOpacity>
  )

  const hasActiveFilter =
    hasActiveDateFilter ||
    filters.sortDirection !== "desc" ||
    isSearchActive ||
    isLocationFilterActive

  return (
    <>
      <View style={$filterBar}>
        {isMobile ? (
          <>
            {/* Mobile: Row 1 — Filtern toggle + Booking */}
            <View style={$buttonGroupMobile}>
              <TouchableOpacity
                style={[
                  $iconButton,
                  $iconButtonMobile,
                  filterHovered && $iconButtonHover,
                  filtersExpanded && $iconButtonActive,
                ]}
                onPress={() => setFiltersExpanded(!filtersExpanded)}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Filter ein-/ausblenden"
                // @ts-ignore - Web-specific
                onMouseEnter={() => setFilterHovered(true)}
                onMouseLeave={() => setFilterHovered(false)}
              >
                <View style={{ position: "relative" }}>
                  <Icon name="filter-list" size={18} color={colors.text} />
                  {hasActiveFilter && <View style={$activeBadge} />}
                </View>
                <Text style={[$iconButtonText, $iconButtonTextMobile]}>Filtern</Text>
                <Icon
                  name={filtersExpanded ? "expand-less" : "expand-more"}
                  size={18}
                  color={colors.text}
                />
              </TouchableOpacity>

              {bookingButton && <View style={$bookingButtonContainer}>{bookingButton}</View>}
            </View>

            {/* Mobile: Row 2 — Expanded filters */}
            {filtersExpanded && (
              <View style={$mobileExpandedFilters}>
                {sortButton}
                {dateButton}
                {locationFilter}
                {searchPlate}
              </View>
            )}
          </>
        ) : (
          /* Desktop: all inline */
          <View style={$buttonGroup}>
            {sortButton}
            {dateButton}
            {locationFilter}
            {searchPlate}
            {bookingButton && <View style={$bookingButtonContainer}>{bookingButton}</View>}
          </View>
        )}
      </View>

      {/* Date Range Calendar Modal */}
      <DateRangeCalendar
        visible={showDateCalendar}
        onClose={() => setShowDateCalendar(false)}
        onApply={handleDateRangeApply}
        onClear={handleClearDateFilter}
        initialRange={filters.dateRange}
      />
    </>
  )
}

// Styles
const $filterBar: ViewStyle = {
  backgroundColor: colors.surface,
  borderBottomWidth: 1,
  borderBottomColor: colors.border,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.md,
}

const $buttonGroup: ViewStyle = {
  flexDirection: "row",
  gap: spacing.sm,
  alignItems: "center",
  justifyContent: "space-between", // Space out Neueste, Datum, and Neu buchen
  width: "100%",
  paddingHorizontal: 14, // Match card inner padding
}

const $buttonGroupMobile: ViewStyle = {
  flexDirection: "row",
  gap: spacing.xs, // Tighter gap on mobile
  alignItems: "center",
  justifyContent: "space-between", // Spread buttons across full width
  width: "100%", // Full width to match order cards
  paddingHorizontal: 14, // Match card inner padding
}

const $iconButton: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  backgroundColor: colors.filterBarSurface,
  borderRadius: 20,
  borderWidth: 0,
  cursor: "pointer" as any,
  // @ts-ignore
  transition: "all 150ms ease",
}

const $iconButtonHover: ViewStyle = {
  backgroundColor: colors.filterBarSurfaceActive,
}

const $iconButtonActive: ViewStyle = {
  backgroundColor: "rgba(255, 56, 47, 0.06)",
  borderWidth: 1,
  borderColor: colors.primary,
}

const $iconButtonText: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.palette.neutral600,
}

const $activeBadge: ViewStyle = {
  display: "none", // Color change on active state is enough
}

// Modal Styles
const $modalOverlay: ViewStyle = {
  flex: 1,
  backgroundColor: colors.overlay,
  justifyContent: "center",
  alignItems: "center",
  padding: spacing.xl,
}

const $modalContent: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 12,
  width: "100%",
  maxWidth: 500,
  // @ts-ignore - Web-specific
  boxShadow: "0 8px 24px rgba(17, 30, 44, 0.25)",
}

const $modalHeader: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
  borderBottomWidth: 1,
  borderBottomColor: colors.border,
}

const $modalTitle: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
}

const $modalBody: ViewStyle = {
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
  gap: spacing.md,
}

const $calendarWrapper: ViewStyle = {
  width: "100%",
}

const $errorContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xs,
  backgroundColor: colors.palette.accent100,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  borderRadius: 8,
  marginTop: spacing.sm,
}

const $errorText: TextStyle = {
  fontSize: 13,
  color: colors.error,
  fontWeight: "500",
}

const $modalFooter: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
  borderTopWidth: 1,
  borderTopColor: colors.border,
}

const $modalButton: ViewStyle = {
  minWidth: 120,
}

const $modalButtonPrimary: ViewStyle = {
  backgroundColor: colors.primary,
}

// Mobile-specific modal styles
const $modalFooterMobile: ViewStyle = {
  flexDirection: "column",
  gap: spacing.sm,
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.md,
  borderTopWidth: 1,
  borderTopColor: colors.border,
}

const $modalButtonGroupMobile: ViewStyle = {
  flexDirection: "column",
  gap: spacing.sm,
  width: "100%",
}

const $modalButtonMobile: ViewStyle = {
  width: "100%",
  minWidth: 0,
}

// Mobile-specific filter button styles
const $iconButtonMobile: ViewStyle = {
  paddingHorizontal: spacing.sm, // Reduced from spacing.md
  paddingVertical: spacing.xs, // Reduced from spacing.sm
}

const $iconButtonTextMobile: TextStyle = {
  fontSize: 12, // Reduced from 14
}

const $bookingButtonContainer: ViewStyle = {
  flexShrink: 0, // Don't shrink the booking button
  marginLeft: "auto", // Push to the right
}

const $mobileExpandedFilters: ViewStyle = {
  flexDirection: "row",
  flexWrap: "wrap",
  gap: spacing.xs,
  marginTop: spacing.xs,
  paddingHorizontal: 14, // Match card inner padding
}
